/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import getConfig from '../../config';
import {getEnvironment} from '../../utils/Environment';

let imslibOnReadyCallback: any = null;
let imslibOnProfileCallback: any = null;
let imslibOnErrorCallback: any = null;
let timeout: number;

function configureImslib(locale, isCCD) {
	window.adobeid = {
		client_id: getConfig('CLIENT_ID'), // eslint-disable-line camelcase
		scope: getConfig('IMS').scopes,
		locale,
		api_parameters: {
			// eslint-disable-line camelcase
			authorize: {
				redirect_uri: window.location.href, // eslint-disable-line camelcase
			},
		},
		onReady() {
			imslibOnReadyCallback();
			clearTimeout(timeout);
		},
		onProfile(profile) {
			imslibOnProfileCallback(profile);
		},
		onError(error) {
			window.cchome.util.logger.error('IMS', 'configureImslib failed', error.toString());

			clearTimeout(timeout);

			if (!isCCD) {
				imslibOnErrorCallback('IMS Failure');
			}
			else {
				imslibOnProfileCallback('IMS Token Failure');
			}
		},
		debug: getEnvironment() !== 'prod',
	};
}

function loadImslib(isCCD) {
	const body = document.getElementsByTagName('body')[0];
	const globalNavElement = document.createElement('script');
	globalNavElement.src = getConfig('IMS').lib;
	globalNavElement.onerror = (error) => {
		window.cchome.util.logger.error('IMS', 'Imslib load failed', error.toString());
		if (!isCCD) {
			imslibOnErrorCallback('IMS Failure');
		}
		else {
			imslibOnProfileCallback('IMS Token Failure');
		}
	};
	body.appendChild(globalNavElement);

	timeout = window.setTimeout(() => {
		if (!isCCD) {
			imslibOnErrorCallback('IMS Timeout');
		}
		else {
			imslibOnProfileCallback('IMS Timeout');
		}
	}, 5000);
}

function ready() {
	return new Promise((resolve) => {
		imslibOnReadyCallback = resolve;
	});
}

function profile() {
	return new Promise((resolve) => {
		imslibOnProfileCallback = resolve;
	});
}

function error() {
	return new Promise((resolve, reject) => {
		imslibOnErrorCallback = reject;
	});
}

class ImsService {
	ready: Promise<any>;
	profile: Promise<any>;
	error: Promise<any>;

	configImsService(locale, isCCD) {
		configureImslib(locale, isCCD);
		this.ready = ready();
		this.profile = profile();
		this.error = error();
		loadImslib(isCCD);
	}

	getReleaseFlags(success, error) {
		return window.adobeIMS && window.adobeIMS.getReleaseFlags(success, error);
	}

	isReleaseFlagActive(bit: number) {
		return window.adobeIMS && window.adobeIMS.isReleaseFlagActive(bit);
	}

	// Note: This casts the underlying adobeIMS.isSignedInUser() result to boolean.
	// You can call adobeIMS.isSignedInUser directly if you need its potentially non-boolean result.
	isSignedInUser(isReAuthentication) {
		return Boolean(window.adobeIMS && window.adobeIMS.isSignedInUser(isReAuthentication));
	}
}

export default new ImsService();
